import React, { Fragment } from 'react';
import { formatDate } from 'utils/format';
import {
    Alert,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDentistOrdersTemplate } from 'apollo/templates/order';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useParams } from 'react-router-dom';
import { Order } from 'models/order';
import Loader from 'components/shared/loader/loader.component';

const OrderHistory = () => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const isLargeScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
    const { id } = useParams<{ tab: string; id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { dentistOrders: orders } = {}, loading } = useQuery(getDentistOrdersTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {
            dentistId: id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const getClinicDisplayName = (order: Order) => {
        if (order.customer?._id !== order.deliveryDetails?.destination?._id) {
            return `${order.customer.name} / ${order.deliveryDetails.destination.name}`;
        }
        return order.customer?.name || '-';
    };

    return (
        <Fragment>
            <Typography mb={2} variant="h2">
                {translate('client-directory:ORDER_HISTORY')}
            </Typography>
            {loading ? (
                <Loader />
            ) : orders?.length ? (
                <TableContainer component={Paper}>
                    <Table size={!isLargeScreen ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell>{translate('orders:CLINIC')}</TableCell>
                                <TableCell>{translate('orders:ORDER_CREATED')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order: Order) => (
                                <TableRow key={order._id}>
                                    <TableCell sx={{ width: 300 }}>
                                        <Link
                                            target="_blank"
                                            href={`/order-details/${order?._id}/main-view`}
                                            sx={{ marginRight: 1 }}
                                        >
                                            {order.orderId}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{getClinicDisplayName(order)}</TableCell>
                                    <TableCell>{formatDate(order.originalCreationDate, 'datetime')}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                    {translate('orders:NO_ORDERS_FOUND')}
                </Alert>
            )}
        </Fragment>
    );
};

export default OrderHistory;
